// Log view: one ISO week at a time — week-average card,
// week nav, filter bar, and a grid with one column per day and one row per
// section (Training/Session RPE/Nutrition/Food Log/Habits/Readiness/Body
// Measurements/Vitals/Progress Photos/Meal Photos). Subphase 2 pivot: the
// section bodies AND the filter bar reuse the athlete-portal's existing
// log/plan calendar components (js/views/athlete-portal/calendar/components/)
// instead of building new ones — same dark theme, icons, i18n and behavior
// already shipped there. LogGrid.js does the section prop-shape adaptation.

import React, { useState, useMemo, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { useTranslation } from "react-i18next";

import {
  weekGroups,
  avgOf,
  toUtcNumber,
  buildWeekDaySlots,
} from "../../../../../utils/nutrition-progress";
import useStickyGridHeader from "../../../../../hooks/useStickyGridHeader";
import CalendarCategoryFilterBar from "../../../calendar/components/CalendarCategoryFilterBar";
import { logCategoryLabel } from "../../../calendar/components/calendarFilterCategories";
import LogWeekAverage from "./LogWeekAverage";
import LogWeekNav from "./LogWeekNav";
import LogGrid, { LogDayHeaderLabel } from "./LogGrid";

// Our 10 sections, in grid row order — must match LogGrid.js's SECTIONS keys
// (which in turn match the colored calendar-filter-pill[data-category] rules
// in calendar-log.css, reused as-is). A subset of LOG_CATEGORY_KEYS
// (calendarFilterCategories.js) — we don't render period (consent-gated,
// out of scope here). Labels reuse logCategoryLabel, the same function the
// athlete's own calendar Log view uses for its filter bar
// (calendar.planView.<key> — short chip labels like "Training"/
// "Measurements"/"Photos", distinct from each card's own fuller header
// title, e.g. "Training Day"/"Body Measurement").
const LOG_FILTER_KEYS = [
  "training",
  "sessionRpe",
  "nutrition",
  "foodLog",
  "habits",
  "readiness",
  "measurements",
  "vitals",
  "photos",
  "mealPhotos",
];

const LogView = ({
  dayRows = [],
  weekGridDayRows,
  dailyStats = [],
  activeExercises = [],
  prExerciseKeysByDay = {},
  repMaxPR,
  habitLogs = [],
  nutritionPlans = [],
  units = "metric",
  dateFrom,
  dateTo,
  onDayClick,
}) => {
  const { t } = useTranslation("athlete-portal");
  // Local state, not URL — same convention as activeView (NutritionProgress)
  // and currentWeekIndex below. Default = every section visible ("All" active).
  const [activeFilters, setActiveFilters] = useState(() => [...LOG_FILTER_KEYS]);
  const handleFilterToggle = useCallback((key) => {
    setActiveFilters((current) =>
      current.includes(key) ? current.filter((k) => k !== key) : [...current, key]
    );
  }, []);
  const handleToggleAll = useCallback(() => setActiveFilters([...LOG_FILTER_KEYS]), []);
  const handleClear = useCallback(() => setActiveFilters([]), []);

  const fromUtc = useMemo(() => toUtcNumber(dateFrom), [dateFrom]);
  const toUtc = useMemo(() => toUtcNumber(dateTo), [dateTo]);
  const todayUtc = useMemo(() => toUtcNumber(new Date()), []);

  // Newest-bucket-first (adherence.js's weekGroups) — index 0 is the most
  // recent week, matching "arranca en la semana más reciente" (confirmed).
  const weekBuckets = useMemo(() => weekGroups(dayRows), [dayRows]);
  const totalWeeks = weekBuckets.length;

  const [currentWeekIndex, setCurrentWeekIndex] = useState(0);
  const weekIndex = Math.min(currentWeekIndex, Math.max(totalWeeks - 1, 0));

  // Builds the day map
  const dayMap = useMemo(
    () => new Map((weekGridDayRows ?? dayRows).map((d) => [d.utcDate, d])),
    [weekGridDayRows, dayRows]
  );
  // Builds the stats by date map
  const statByDate = useMemo(
    () => new Map(dailyStats.map((s) => [s.utcDate, s])),
    [dailyStats]
  );
  // Builds the habit logs by date map
  const habitLogsByDate = useMemo(
    () => new Map(habitLogs.map((h) => [h.utcDate, h])),
    [habitLogs]
  );
  // Builds the exercises by date map
  const exercisesByDate = useMemo(() => {
    const map = new Map();
    activeExercises.forEach((ex) => {
      const list = map.get(ex.utcDate) ?? [];
      list.push(ex);
      map.set(ex.utcDate, list);
    });
    return map;
  }, [activeExercises]);

  // Expand the current bucket into its full Mon–Sun ISO week via the engine's
  // buildWeekDaySlots (candidate to unify with CalendarView's weekRows logic
  // on a future reuse pass). The 4 per-date Maps above stay independently
  // memoized so navigating weeks only rebuilds this assembly step, not the
  // Maps themselves. Days outside the reporting period are flagged
  // outOfRange; LogGrid renders them dimmed, date only.
  const weekDays = useMemo(() => {
    const bucket = weekBuckets[weekIndex];
    if (!bucket?.length) return [];
    const monday = Number(
      moment(String(bucket[0].utcDate), "YYYYMMDD").startOf("isoWeek").format("YYYYMMDD")
    );
    return buildWeekDaySlots(monday, {
      dayMap,
      statByDate,
      exercisesByDate,
      habitLogsByDate,
      fromUtc,
      toUtc,
    });
  }, [
    weekBuckets,
    weekIndex,
    dayMap,
    statByDate,
    exercisesByDate,
    habitLogsByDate,
    fromUtc,
    toUtc,
  ]);

  const weekAvg = useMemo(() => avgOf(weekBuckets[weekIndex] ?? []), [weekBuckets, weekIndex]);

  // Sticky day-header — see useStickyGridHeader.js for why this needs JS
  // instead of CSS `position: sticky` (`.np-log-grid-scroll`'s
  // overflow-x:auto traps it the same way `.np-table-grid-scroll` did on
  // Table). getHeaderCells measures each day's whole card (.np-log-day),
  // not just its inner date label — the card owns the left/right padding,
  // so the CARD's width is what actually matches the grid's column tracks;
  // measuring just the date label would miss that padding and misalign the
  // clone against the real columns below. deps: weekIndex — the day cards
  // remount (new key={day.utcDate}) on every week nav, so the previous
  // cards' DOM nodes go stale and need a fresh query + re-observe.
  const gridScrollRef = useRef(null);
  const gridDaysRef = useRef(null);
  const { sentinelRef, stickyHeadRef, pinned, colWidths, headerRect, onScrollSync } = useStickyGridHeader({
    scrollRef: gridScrollRef,
    containerRef: gridDaysRef,
    getHeaderCells: (grid) => Array.from(grid.querySelectorAll(".np-log-day")),
    deps: [weekIndex],
  });

  if (!totalWeeks) {
    return null;
  }

  return (
    <div>
      <div className="np-log-header">
        <LogWeekAverage avg={weekAvg} />
        <div className="np-log-header__right">
          <div className="np-log-header__nav-row">
            <LogWeekNav
              startUtc={weekDays[0]?.utcDate}
              endUtc={weekDays[6]?.utcDate}
              weekNumber={totalWeeks - weekIndex}
              totalWeeks={totalWeeks}
              onPrev={() => setCurrentWeekIndex((i) => Math.min(i + 1, totalWeeks - 1))}
              onNext={() => setCurrentWeekIndex((i) => Math.max(i - 1, 0))}
            />
          </div>
          {/* Dedicated styling hook for the filter bar's Log-only CSS
              (nutrition.css) — kept separate from np-log-header__right, which
              is a layout wrapper that could get restructured independently. */}
          <div className="np-log-filter-bar">
            <CalendarCategoryFilterBar
              activeFilters={activeFilters}
              onToggle={handleFilterToggle}
              onToggleAll={handleToggleAll}
              onClear={handleClear}
              categoryKeys={LOG_FILTER_KEYS}
              labelForKey={logCategoryLabel}
              hint={t("nutritionPage.progress.log.filterHint")}
            />
          </div>
        </div>
      </div>

      {pinned && (
        <div className="np-log-sticky-head-fixed" style={{ left: headerRect.left, width: headerRect.width }}>
          <div
            className="np-log-sticky-head-inner"
            ref={stickyHeadRef}
            style={{ gridTemplateColumns: colWidths.map((w) => `${w}px`).join(" ") }}
          >
            {weekDays.map((day) => (
              <div key={day.utcDate} className="np-log-sticky-head-cell">
                <LogDayHeaderLabel day={day} todayUtc={todayUtc} />
              </div>
            ))}
          </div>
        </div>
      )}

      <div ref={sentinelRef} />
      <div className="np-log-grid-scroll" ref={gridScrollRef} onScroll={onScrollSync}>
        <LogGrid
          gridRef={gridDaysRef}
          weekDays={weekDays}
          todayUtc={todayUtc}
          nutritionPlans={nutritionPlans}
          units={units}
          prExerciseKeysByDay={prExerciseKeysByDay}
          repMaxPR={repMaxPR}
          activeFilters={activeFilters}
          onDayClick={onDayClick}
        />
      </div>
    </div>
  );
};

LogView.propTypes = {
  dayRows: PropTypes.arrayOf(PropTypes.object),
  weekGridDayRows: PropTypes.arrayOf(PropTypes.object),
  dailyStats: PropTypes.arrayOf(PropTypes.object),
  activeExercises: PropTypes.arrayOf(PropTypes.object),
  prExerciseKeysByDay: PropTypes.object,
  repMaxPR: PropTypes.object,
  habitLogs: PropTypes.arrayOf(PropTypes.object),
  nutritionPlans: PropTypes.arrayOf(PropTypes.object),
  units: PropTypes.string,
  dateFrom: PropTypes.instanceOf(Date),
  dateTo: PropTypes.instanceOf(Date),
  onDayClick: PropTypes.func,
};

export default LogView;
